import { MRTGrid } from "@foodpilot/foods";
import { MRT_ColumnDef, MRT_PaginationState, MRT_RowData, MRT_SortingState } from "material-react-table";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

type RowNumber = number;
type IsSelected = boolean;

type RowSelectionState<EntityForm, EntityList> = {
    rowSelection: Record<RowNumber, IsSelected>;
    existingEntitiesMapping: Record<RowNumber, EntityForm | undefined>;
    entityListMapping: Record<RowNumber, EntityList>;
};

export const getInitialSelection = <EntityForm, EntityList extends MRT_RowData>(
    entityList: EntityList[] | undefined,
    existingEntities: EntityForm[],
    selectionHandler: (existingEntity: EntityForm) => string,
) => {
    const emptyRowSelectionState: RowSelectionState<EntityForm, EntityList> = {
        rowSelection: {},
        existingEntitiesMapping: {},
        entityListMapping: {},
    };

    if (entityList === undefined) return emptyRowSelectionState;

    return entityList.reduce((acc, entity, rowNumber) => {
        const foundExistingEnt = existingEntities.find((ent) => {
            return selectionHandler(ent) === entity["@id"];
        });
        const isFound = foundExistingEnt !== undefined;

        acc["rowSelection"][rowNumber] = isFound;
        acc["existingEntitiesMapping"][rowNumber] = foundExistingEnt;
        acc["entityListMapping"][rowNumber] = entity;

        return acc;
    }, emptyRowSelectionState);
};

export type ListGridProps<EntityForm, EntityList extends MRT_RowData> = {
    data: EntityList[];
    columns: MRT_ColumnDef<EntityList, any>[];
    delayedSearch: string;
    existingEntities: EntityForm[];
    setNewEntities: (entities: EntityForm[]) => void;
    selectionHandler: (existingEntity: EntityForm) => string;
    newEntityHandler: (entityList: EntityList) => EntityForm;
    isLoading: boolean;
    totalItems: number;
    pagination: MRT_PaginationState;
    setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
    sorting: MRT_SortingState;
    setSorting: Dispatch<SetStateAction<MRT_SortingState>>;
};
export function ListGrid<EntityForm, EntityList extends MRT_RowData>(props: ListGridProps<EntityForm, EntityList>) {
    const {
        data,
        columns,
        delayedSearch,
        existingEntities,
        setNewEntities,
        selectionHandler,
        newEntityHandler,
        isLoading,
        totalItems,
        pagination,
        setPagination,
        sorting,
        setSorting,
    } = props;

    const initialSelection = getInitialSelection(data, existingEntities, selectionHandler);
    const [rowSelection, setRowSelection] = useState(initialSelection["rowSelection"]);

    useEffect(() => {
        setRowSelection(initialSelection["rowSelection"]);
    }, [data]);

    useEffect(() => {
        // The RowSelection is a Record<RowNumber, ...>;
        // The problem comes from MRT actually deleting the key, instead of setting it to False
        // when we unselect the element.
        // So we want to make sure the keys who don't exist are still set to at least FALSE;
        const rowSelectionFixed = Array(pagination.pageSize)
            .fill(undefined)
            .map((_, index) => index);
        const rowSelectionArray = rowSelectionFixed.map((row) => {
            const rowString = row.toString();
            const selectedRow = rowSelection[row];
            if (selectedRow !== undefined) {
                return [rowString, selectedRow];
            }
            return [rowString, false];
        });
        // const rowSelectionArray = Object.entries(rowSelection);
        if (rowSelectionArray.length === 0) {
            // Means the component is not loaded yet.
            return;
        }

        const existingEntityCopy = [...existingEntities];
        const newEntities = rowSelectionArray.reduce((previousValue, [rowNumber, isSelected]) => {
            if (isSelected === false) {
                // Since it's NOT selected, we want to DELETE the item if it existed.
                const row = Number(rowNumber);
                const existingEnt = initialSelection["existingEntitiesMapping"][row];
                if (existingEnt === undefined) return previousValue;

                return previousValue.filter((item) => selectionHandler(item) !== selectionHandler(existingEnt));
            } else {
                // The item is actively selected.
                const row = Number(rowNumber);
                const existingEnt = initialSelection["existingEntitiesMapping"][row];

                // Does it already exists ?
                if (existingEnt !== undefined) return previousValue;

                // Now we're sure we just selected it.
                const entityList = initialSelection["entityListMapping"][row];
                const newEntity = newEntityHandler(entityList);

                return [...previousValue, newEntity];
            }
        }, existingEntityCopy);

        // Here, we have the Full previous selected ingredients
        // In addition of the new ones;
        // In deletion of those that were deleted;
        setNewEntities(newEntities);
    }, [rowSelection]);

    return (
        <MRTGrid
            columns={columns}
            data={data}
            enablePagination={true}
            enableBottomToolbar={false}
            enableRowSelection={true}
            onRowSelectionChange={setRowSelection}
            initialState={{
                pagination: { pageIndex: 0, pageSize: 8 },
                rowSelection: rowSelection,
            }}
            state={{
                globalFilter: delayedSearch,
                rowSelection: rowSelection,
                isLoading: isLoading,
                pagination: pagination,
                sorting: sorting,
            }}
            rowCount={totalItems}
            manualPagination={true}
            onPaginationChange={setPagination}
            manualSorting={true}
            onSortingChange={setSorting}
        />
    );
}
